import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Key, Radio } from "lucide-react";

import { serviceKeys, type ServiceKey } from "../api/staff";

export function ServiceKeysList() {
  const [items, setItems] = useState<ServiceKey[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void refresh();
  }, []);

  async function refresh() {
    try {
      const out = await serviceKeys.list();
      setItems(out.items);
    } catch (err) {
      setError(err instanceof Error ? err.message : "load failed");
    }
  }

  async function onRevoke(id: string) {
    if (!confirm(`Revoke service key ${id}? This cannot be undone.`)) return;
    try {
      await serviceKeys.revoke(id);
      await refresh();
    } catch (err) {
      alert(err instanceof Error ? err.message : "revoke failed");
    }
  }

  return (
    <div>
      {/* Service Keys & Tokens Navigation Tabs */}
      <div className="flex items-center gap-2 border-b border-border mb-6 pb-2">
        <Link
          to="/service-keys"
          className="px-3.5 py-1.5 rounded-lg text-xs font-bold bg-primary text-primary-foreground shadow-xs flex items-center gap-1.5"
        >
          <Key className="w-3.5 h-3.5" />
          Clés API Serveur
        </Link>
        <Link
          to="/discord"
          className="px-3.5 py-1.5 rounded-lg text-xs font-semibold text-muted-foreground hover:bg-accent hover:text-foreground transition-colors flex items-center gap-1.5"
        >
          <Radio className="w-3.5 h-3.5" />
          Discord Tokens & Tracker
        </Link>
      </div>

      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Service keys</h1>
          <p className="mt-1 text-sm text-text-secondary">
            Server-to-server API credentials. Every key must pass a
            terminal validation run before activation.
          </p>
        </div>
        <Link to="/service-keys/new" className="btn-primary">
          + New key
        </Link>
      </div>
      {error ? (
        <div className="card mb-4 border-red-500/30 bg-red-500/10 text-red-300">
          {error}
        </div>
      ) : null}
      <div className="card overflow-hidden p-0">
        <table className="w-full text-sm">
          <thead className="bg-bg-elevated text-left text-xs uppercase text-text-muted">
            <tr>
              <th className="px-4 py-3">Name</th>
              <th className="px-4 py-3">Role</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3">Permissions</th>
              <th className="px-4 py-3">Created</th>
              <th className="px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {items == null ? (
              <tr>
                <td colSpan={6} className="px-4 py-6 text-center text-text-muted">
                  Loading…
                </td>
              </tr>
            ) : items.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-4 py-6 text-center text-text-muted">
                  No service keys yet.
                </td>
              </tr>
            ) : (
              items.map((k) => (
                <tr key={k.id} className="border-t border-white/5">
                  <td className="px-4 py-3">
                    <div className="font-medium">{k.name}</div>
                    <div className="text-xs text-text-muted">{k.prefix}_…</div>
                  </td>
                  <td className="px-4 py-3">{k.role}</td>
                  <td className="px-4 py-3">
                    <StatusPill status={k.status} />
                  </td>
                  <td className="px-4 py-3 text-xs text-text-secondary">
                    {k.permissions.join(", ")}
                  </td>
                  <td className="px-4 py-3 text-xs text-text-muted">
                    {new Date(k.createdAt).toLocaleString()}
                  </td>
                  <td className="px-4 py-3 text-right">
                    {k.status === "pending" && k.latestRunId ? (
                      <Link
                        to={`/service-keys/${k.id}/runs/${k.latestRunId}`}
                        className="text-accent hover:underline"
                      >
                        View run
                      </Link>
                    ) : null}
                    {k.status !== "revoked" && k.status !== "rejected" ? (
                      <button
                        className="ml-3 text-red-400 hover:underline"
                        onClick={() => onRevoke(k.id)}
                      >
                        Revoke
                      </button>
                    ) : null}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function StatusPill({ status }: { status: ServiceKey["status"] }) {
  const tone =
    status === "active"
      ? "bg-green-500/15 text-green-300"
      : status === "pending"
        ? "bg-amber-500/15 text-amber-300"
        : status === "rejected"
          ? "bg-red-500/15 text-red-300"
          : "bg-white/5 text-text-muted";
  return (
    <span className={`inline-flex rounded-full px-2 py-0.5 text-xs ${tone}`}>
      {status}
    </span>
  );
}
